import { useEffect, useState } from "react";
import { X, Clock, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { firstName, fmtLongDate } from "@/lib/rgApi";

/**
 * EventEditModal — Modale légère pour consulter un événement du calendrier
 * (🧺 Marché · 🌸 Bain fleurs · 🌹 Décor · 🥐 Petit-déj) et modifier son heure.
 *
 * Événements gérés :
 *   • marche       (date + heure modifiables)
 *   • bain_fleurs  (heure seule ; date = jour d'arrivée)
 *   • decor        (heure seule ; date = jour d'arrivée)
 *   • breakfast    (heure seule ; date = chaque matin du séjour)
 *
 * Enregistrement via onSaveOverride(kind, {date?, time}) qui doit patcher
 * reservation.event_overrides[kind].
 */
const KIND_LABELS = {
  marche: { icon: "🧺", label: "Marché", canEditDate: true, endMinutes: 60 },
  bain_fleurs: { icon: "🌸", label: "Bain de fleurs", canEditDate: false, endMinutes: 30 },
  decor: { icon: "🌹", label: "Décor du refuge", canEditDate: false, endMinutes: 60 },
  breakfast: { icon: "🥐", label: "Petit-déjeuner", canEditDate: false, endMinutes: 60 },
};

export default function EventEditModal({ open, onClose, event, reservation, onSaveOverride }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && event) {
      setDate(event.date || "");
      setTime(event.time || "");
    }
  }, [open, event]);

  if (!open || !event || !reservation) return null;
  const meta = KIND_LABELS[event.kind] || { icon: "•", label: "Événement", canEditDate: false, endMinutes: 60 };

  const endTime = (() => {
    if (!time) return "";
    const [h, m] = time.split(":").map(Number);
    const total = h * 60 + m + meta.endMinutes;
    const eh = Math.min(23, Math.floor(total / 60));
    const em = total % 60;
    return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
  })();

  const save = async () => {
    setBusy(true);
    try {
      const payload = { time };
      if (meta.canEditDate && date) payload.date = date;
      await onSaveOverride(event.kind, payload);
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="rg-modal-back" onClick={onClose} data-testid="rg-eventedit-back" />
      <div className="rg-modal" role="dialog" aria-modal="true" data-testid="rg-eventedit-modal">
        <div className="rg-modal-head">
          <div>
            <div className="rg-tiny">{meta.icon} {meta.label}</div>
            <div className="rg-modal-title">{firstName(reservation.name)} · {fmtLongDate(event.date)}</div>
          </div>
          <button className="rg-icon-btn" onClick={onClose} data-testid="rg-eventedit-close"><X size={18} /></button>
        </div>

        <div className="rg-modal-body">
          <div className="rg-modal-section">
            <label className="rg-tiny">Horaire</label>
            <div className="rg-row" style={{ gap: 8, marginTop: 4 }}>
              <Clock size={14} style={{ color: "var(--rg-text-2)" }} />
              <input
                type="time"
                className="rg-input"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                style={{ width: 140 }}
                data-testid="rg-eventedit-time"
              />
              <span className="rg-muted" style={{ fontSize: 12 }}>→ {endTime || "—"}</span>
            </div>

            {meta.canEditDate && (
              <>
                <label className="rg-tiny" style={{ marginTop: 12, display: "block" }}>Date</label>
                <input
                  type="date"
                  className="rg-input"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={{ width: 180, marginTop: 4 }}
                  data-testid="rg-eventedit-date"
                />
              </>
            )}
          </div>

          <div className="rg-row" style={{ gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            <button className="rg-btn" onClick={save} disabled={busy || !time} data-testid="rg-eventedit-save">
              Enregistrer
            </button>
            <Link
              to={`/admin/gestion/voyageur/${reservation.id}`}
              className="rg-btn ghost"
              data-testid="rg-eventedit-open-guest"
            >
              <ExternalLink size={13} /> Fiche voyageur
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
